/**
 * Article loader hook — dynamically imports the content module for the
 * given (cc, lang, topic) triple via the Vite-glob registry.  Returns
 * null while loading or when no module exists for the combination.
 */

import { useEffect, useState } from 'react';
import {
  findHubLoader,
  findTopicLoader,
} from './content-registry';
import type { CountryCode, LangCode, TopicSlug } from './architecture';
import type { HubArticle, TopicArticle } from './content-types';

export function useHubArticle(
  cc: CountryCode | null,
  lang: LangCode,
): { article: HubArticle | null; loading: boolean } {
  const [state, setState] = useState<{ article: HubArticle | null; loading: boolean }>({
    article: null,
    loading: false,
  });

  useEffect(() => {
    if (!cc) return;
    const loader = findHubLoader(cc, lang);
    if (!loader) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((s) => ({ ...s, loading: true }));
    loader()
      .then((m) => {
        if (!cancelled) setState({ article: m.default, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ article: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [cc, lang]);

  if (!cc) return { article: null, loading: false };
  if (!findHubLoader(cc, lang)) return { article: null, loading: false };
  return state;
}

export function useTopicArticle(
  cc: CountryCode | null,
  topic: TopicSlug | null,
  lang: LangCode,
): { article: TopicArticle | null; loading: boolean } {
  const [state, setState] = useState<{ article: TopicArticle | null; loading: boolean }>({
    article: null,
    loading: false,
  });

  useEffect(() => {
    if (!cc || !topic) return;
    const loader = findTopicLoader(cc, lang, topic);
    if (!loader) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((s) => ({ ...s, loading: true }));
    loader()
      .then((m) => {
        if (!cancelled) setState({ article: m.default, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ article: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [cc, topic, lang]);

  if (!cc || !topic) return { article: null, loading: false };
  if (!findTopicLoader(cc, lang, topic)) return { article: null, loading: false };
  return state;
}
